import './Admin.scss';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RegisterRecruit from './recruit/RegisterRecruit';
import RecruitListManagePre from './recruitemail/RecruitEmailManage';
import RecruitSeoReview from './recruit/RecruitSeoReview';
import ChurchVerifyManage from './recruit/ChurchVerifyManage';
import AggregatedManage from './recruit/AggregatedManage';
import RecruitAutoPublish from './recruit/RecruitAutoPublish';
import MinisterManage from './minister/MinisterManage';
import AdminResumeWrite from './minister/AdminResumeWrite';
import PastorCallAdmin from './pastor/PastorCallAdmin';
import type { PastorCallTab } from './pastor/PastorCallAdmin';
import WorshipManage from './worship/WorshipManage';
import AdminEmail from './email_adv/AdminEmail';
import PushNotificationPage from './appmanage/PushNotificationPage';
import AppVersionPage from './appmanage/AppVersionPage';
import MainSlideAdmin from './appmanage/MainSlideAdmin';
import AdminStatistics from './statistics/AdminStatistics';
import Backup from './Backup';
import BoardPostWrite from './board/BoardPostWrite';
import BoardPostManage from './board/BoardPostManage';
import RetreatManage from './retreat/RetreatManage';
import AdminUser from './user/AdminUser';
import AdminInquiryManage from './user/AdminInquiryManage';
import { clearAdminSession, getAdminDisplayName, getAdminSession, isSuperAdmin } from './adminSession';

type MenuKey =
  | 'registerrecruit'
  | 'recruitlistmanagepre'
  | 'recruitseoreview'
  | 'churchverify'
  | 'aggregatedmanage'
  | 'recruitautopublish'
  | 'ministermanage'
  | 'resumewrite'
  | 'pastorcall'
  | 'pastorcallpastors'
  | 'pastorcallemail'
  | 'pastorcallpromotions'
  | 'pastorcallconsults'
  | 'worshipmanage'
  | 'emailmanage'
  | 'pushnotifi'
  | 'appversion'
  | 'mainslide'
  | 'noticepost'
  | 'boardpostmanage'
  | 'retreatmanage'
  | 'adminuser'
  | 'admininquiry'
  | 'adminmanage'
  | 'backup';

type MenuGroupKey =
  | 'crawling-management'
  | 'minister-management'
  | 'pastorcall-management'
  | 'board-management'
  | 'owner-only';

type MenuItem = {
  key: MenuKey;
  label: string;
  adminOnly?: boolean;
  superOnly?: boolean;
  icon: string;
};

type MenuGroup = {
  key: MenuGroupKey;
  label: string;
  icon: string;
  childKeys: MenuKey[];
  bottom?: boolean;
};

const MENU_ITEMS: MenuItem[] = [
  { key: 'adminmanage', label: '대시보드', icon: '📊' },
  { key: 'registerrecruit', label: '구인구직 관리', icon: '📄' },
  { key: 'recruitautopublish', label: '자동 게시', icon: '🤖' },
  { key: 'recruitlistmanagepre', label: '메일 전송', icon: '📧' },
  { key: 'aggregatedmanage', label: '공고 관리', icon: '🔒' },
  { key: 'recruitseoreview', label: '공고 정형화 검수', icon: '🧹' },
  { key: 'churchverify', label: '교회 인증 관리', icon: '✅' },
  { key: 'ministermanage', label: '이력서 관리', icon: '🙋' },
  { key: 'resumewrite', label: '이력서 작성하기', icon: '✍️' },
  { key: 'pastorcallpastors', label: '등록 사역자', icon: '⛪' },
  { key: 'pastorcallemail', label: '사역자 이메일 홍보', icon: '⛪' },
  { key: 'pastorcall', label: '청빙 의뢰', icon: '⛪' },
  { key: 'pastorcallpromotions', label: '공고 홍보', icon: '⛪' },
  { key: 'pastorcallconsults', label: '상담 신청', icon: '⛪' },
  { key: 'worshipmanage', label: '예배사역 관리', icon: '🎵' },
  { key: 'emailmanage', label: '광고 이메일 발송', icon: '✉️' },
  { key: 'appversion', label: '앱 버전', icon: '📱', superOnly: true },
  { key: 'noticepost', label: '게시판 글 작성', icon: '📢' },
  { key: 'boardpostmanage', label: '게시글 관리', icon: '📋' },
  { key: 'retreatmanage', label: '수련회 관리', icon: '🏕️' },
  { key: 'adminuser', label: '회원 관리', icon: '👤' },
  { key: 'admininquiry', label: '고객문의 관리', icon: '💬' },
  { key: 'pushnotifi', label: '앱 푸시 알림', icon: '🔔' },
  { key: 'mainslide', label: '메인 슬라이드', icon: '🖼️' },
  { key: 'backup', label: '백업', adminOnly: true, icon: '🗄️' },
];

const MENU_GROUPS: MenuGroup[] = [
  {
    key: 'crawling-management',
    label: '크롤링 관리',
    icon: '🕷️',
    childKeys: ['recruitautopublish', 'recruitlistmanagepre', 'aggregatedmanage'],
  },
  {
    key: 'minister-management',
    label: '사역자정보 관리',
    icon: '🙋',
    childKeys: ['ministermanage', 'resumewrite'],
  },
  {
    key: 'pastorcall-management',
    label: '담임청빙 중개',
    icon: '⛪',
    childKeys: ['pastorcallpastors', 'pastorcallemail', 'pastorcall', 'pastorcallpromotions', 'pastorcallconsults'],
  },
  {
    key: 'board-management',
    label: '게시판 관리',
    icon: '📝',
    childKeys: ['noticepost', 'boardpostmanage'],
  },
  {
    key: 'owner-only',
    label: '대표자 전용',
    icon: '🔒',
    childKeys: ['appversion', 'backup'],
    bottom: true,
  },
];

const PASTOR_CALL_TAB_BY_MENU: Record<'pastorcall' | 'pastorcallpastors' | 'pastorcallemail' | 'pastorcallpromotions' | 'pastorcallconsults', PastorCallTab> = {
  pastorcall: 'requests',
  pastorcallpastors: 'pastors',
  pastorcallemail: 'email',
  pastorcallpromotions: 'promotions',
  pastorcallconsults: 'consults',
};

function canViewMenuItem(item: MenuItem, session: ReturnType<typeof getAdminSession>): boolean {
  if (item.superOnly) return isSuperAdmin(session);
  if (item.adminOnly) return isSuperAdmin(session);
  return true;
}

function AdminMainContent({
  activeMenu,
  adminSession,
  onChangeMenu,
}: {
  activeMenu: MenuKey;
  adminSession: ReturnType<typeof getAdminSession>;
  onChangeMenu: (menu: MenuKey) => void;
}) {
  if (activeMenu === 'appversion' && !isSuperAdmin(adminSession)) {
    return (
      <p className="admin-main-layout__forbidden">이 메뉴는 최종관리자(대표자)만 이용할 수 있습니다.</p>
    );
  }

  switch (activeMenu) {
    case 'registerrecruit':
      return <RegisterRecruit />;
    case 'recruitlistmanagepre':
      return <RecruitListManagePre />;
    case 'recruitseoreview':
      return <RecruitSeoReview />;
    case 'churchverify':
      return <ChurchVerifyManage />;
    case 'aggregatedmanage':
      return <AggregatedManage />;
    case 'recruitautopublish':
      return <RecruitAutoPublish />;
    case 'ministermanage':
      return <MinisterManage />;
    case 'resumewrite':
      return <AdminResumeWrite onDone={() => onChangeMenu('ministermanage')} />;
    case 'pastorcall':
    case 'pastorcallpastors':
    case 'pastorcallemail':
    case 'pastorcallpromotions':
    case 'pastorcallconsults':
      return (
        <PastorCallAdmin
          tab={PASTOR_CALL_TAB_BY_MENU[activeMenu]}
          onShowRequests={() => onChangeMenu('pastorcall')}
        />
      );
    case 'worshipmanage':
      return <WorshipManage />;
    case 'emailmanage':
      return <AdminEmail />;
    case 'pushnotifi':
      return <PushNotificationPage />;
    case 'appversion':
      return <AppVersionPage />;
    case 'mainslide':
      return <MainSlideAdmin />;
    case 'noticepost':
      return <BoardPostWrite />;
    case 'boardpostmanage':
      return <BoardPostManage />;
    case 'retreatmanage':
      return <RetreatManage />;
    case 'adminuser':
      return <AdminUser />;
    case 'admininquiry':
      return <AdminInquiryManage />;
    case 'adminmanage':
      return <AdminStatistics />;
    case 'backup':
      return <Backup />;
    default:
      return <RegisterRecruit />;
  }
}

export default function Main(props: any) {
  const navigate = useNavigate();
  const adminSession = getAdminSession();
  const superAdmin = isSuperAdmin(adminSession);
  const [activeMenu, setActiveMenu] = useState<MenuKey>('adminmanage');
  const [openMenuGroups, setOpenMenuGroups] = useState<Record<MenuGroupKey, boolean>>({
    'crawling-management': false,
    'minister-management': false,
    'pastorcall-management': false,
    'board-management': false,
    'owner-only': false,
  });

  const visibleMenus = useMemo(
    () => MENU_ITEMS.filter((item) => canViewMenuItem(item, adminSession)),
    [adminSession]
  );

  useEffect(() => {
    const activeMenuVisible = visibleMenus.some((menu) => menu.key === activeMenu);
    if (!activeMenuVisible && visibleMenus.length > 0) {
      setActiveMenu(visibleMenus[0].key);
    }
  }, [activeMenu, visibleMenus]);

  useEffect(() => {
    const parentGroup = MENU_GROUPS.find((group) => group.childKeys.includes(activeMenu));
    if (!parentGroup) return;
    setOpenMenuGroups((prev) => ({ ...prev, [parentGroup.key]: true }));
  }, [activeMenu]);

  const visibleMenuMap = useMemo(
    () => new Map(visibleMenus.map((menu) => [menu.key, menu] as const)),
    [visibleMenus]
  );

  const groupedChildKeys = useMemo(
    () => new Set(MENU_GROUPS.flatMap((group) => group.childKeys)),
    []
  );

  const firstChildGroupMap = useMemo(() => {
    const map = new Map<MenuKey, MenuGroup>();
    MENU_GROUPS.forEach((group) => {
      const firstVisibleChild = group.childKeys.find((key) => visibleMenuMap.has(key));
      if (firstVisibleChild) map.set(firstVisibleChild, group);
    });
    return map;
  }, [visibleMenuMap]);

  const toggleMenuGroup = (groupKey: MenuGroupKey) => {
    setOpenMenuGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const normalVisibleMenus = useMemo(
    () => visibleMenus.filter((menu) => !MENU_GROUPS.some((group) => group.bottom && group.childKeys.includes(menu.key))),
    [visibleMenus]
  );

  const bottomGroups = useMemo(() => MENU_GROUPS.filter((group) => group.bottom), []);

  const handleLogout = () => {
    clearAdminSession();
    navigate('/admin');
    window.scrollTo(0, 0);
  };

  return (
    <div className="AdminContainer admin-main-shell">
      <header className="admin-main-shell__topbar">
        <div className="admin-main-shell__brand">사역자모아 Admin</div>
        <div className="admin-main-shell__user">
          <button
            type="button"
            className="admin-main-shell__home-btn"
            onClick={() => navigate('/')}
          >
            홈
          </button>
          <button
            type="button"
            className="admin-main-shell__home-btn admin-main-shell__logout-btn"
            onClick={handleLogout}
          >
            로그아웃
          </button>
          <span className="admin-main-shell__user-name">
            {getAdminDisplayName(adminSession) || 'admin'}
          </span>
          <span className="admin-main-shell__user-role">
            {superAdmin ? '최종관리자' : '관리자'}
          </span>
        </div>
      </header>

      <div className="admin-main-layout">
        <aside className="admin-main-layout__sidebar">
          <div className="admin-main-layout__sidebar-title">관리자 메뉴</div>
          <nav className="admin-main-layout__menu">
            {normalVisibleMenus.map((menu) => {
              const group = firstChildGroupMap.get(menu.key);
              if (group) {
                const childMenus = group.childKeys
                  .map((key) => visibleMenuMap.get(key))
                  .filter((item): item is MenuItem => Boolean(item));
                if (childMenus.length === 0) return null;

                const isOpen = openMenuGroups[group.key];
                const isGroupActive = childMenus.some((child) => child.key === activeMenu);

                return (
                  <div key={group.key} className="admin-main-layout__menu-group">
                    <button
                      type="button"
                      className={`admin-main-layout__menu-btn admin-main-layout__menu-parent${
                        isGroupActive ? ' is-active' : ''
                      }`}
                      onClick={() => toggleMenuGroup(group.key)}
                    >
                      <span className="admin-main-layout__menu-icon" aria-hidden>{group.icon}</span>
                      <span>{group.label}</span>
                      <span className={`admin-main-layout__menu-caret${isOpen ? ' is-open' : ''}`}>▾</span>
                    </button>
                    {isOpen && (
                      <div className="admin-main-layout__submenu">
                        {childMenus.map((childMenu) => (
                          <button
                            key={childMenu.key}
                            type="button"
                            className={`admin-main-layout__menu-btn admin-main-layout__submenu-btn${
                              activeMenu === childMenu.key ? ' is-active' : ''
                            }`}
                            onClick={() => {
                              window.scrollTo(0, 0);
                              setActiveMenu(childMenu.key);
                            }}
                          >
                            <span>{childMenu.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              if (groupedChildKeys.has(menu.key)) return null;

              return (
                <button
                  key={menu.key}
                  type="button"
                  className={`admin-main-layout__menu-btn${activeMenu === menu.key ? ' is-active' : ''}`}
                  onClick={() => {
                    window.scrollTo(0, 0);
                    setActiveMenu(menu.key);
                  }}
                >
                  <span className="admin-main-layout__menu-icon" aria-hidden>{menu.icon}</span>
                  <span>{menu.label}</span>
                </button>
              );
            })}

            {bottomGroups.map((group) => {
              const childMenus = group.childKeys
                .map((key) => visibleMenuMap.get(key))
                .filter((item): item is MenuItem => Boolean(item));
              if (childMenus.length === 0) return null;

              const isOpen = openMenuGroups[group.key];
              const isGroupActive = childMenus.some((child) => child.key === activeMenu);

              return (
                <div key={group.key} className="admin-main-layout__menu-group admin-main-layout__menu-group--bottom">
                  <button
                    type="button"
                    className={`admin-main-layout__menu-btn admin-main-layout__menu-parent${
                      isGroupActive ? ' is-active' : ''
                    }`}
                    onClick={() => toggleMenuGroup(group.key)}
                  >
                    <span className="admin-main-layout__menu-icon" aria-hidden>{group.icon}</span>
                    <span>{group.label}</span>
                    <span className={`admin-main-layout__menu-caret${isOpen ? ' is-open' : ''}`}>▾</span>
                  </button>
                  {isOpen && (
                    <div className="admin-main-layout__submenu">
                      {childMenus.map((childMenu) => (
                        <button
                          key={childMenu.key}
                          type="button"
                          className={`admin-main-layout__menu-btn admin-main-layout__submenu-btn${
                            activeMenu === childMenu.key ? ' is-active' : ''
                          }`}
                          onClick={() => {
                            window.scrollTo(0, 0);
                            setActiveMenu(childMenu.key);
                          }}
                        >
                          <span>{childMenu.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        <section className="admin-main-layout__content">
          <div className="admin-main-layout__content-body">
            <AdminMainContent activeMenu={activeMenu} adminSession={adminSession} onChangeMenu={setActiveMenu} />
          </div>
        </section>
      </div>
    </div>
  );
}
